/**
 =========================================================
 * Material Dashboard 2 React - v2.1.0
 =========================================================
 
 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)
 
 Coded by www.creative-tim.com
 
 =========================================================
 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import { useEffect, useState } from 'react';

// react-github-btn
// @mui material components
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import Icon from '@mui/material/Icon';

// @mui icons
// Material Dashboard 2 React components
import { useIntl } from 'react-intl';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

// Custom styles for the Configurator
import ConfiguratorRoot from 'components/Configurator/ConfiguratorRoot'; // Material Dashboard 2 React context
import {
  resetTheme,
  setDarkMode,
  setFixedNavbar,
  setOpenConfigurator,
  setPrimaryColor,
  setSecondaryColor,
  setTransparentSidenav,
  setWhiteSidenav,
  useMaterialUIController
} from 'context';
import PopoverPicker from 'components/Pickers/ColorPicker/PopoverPicker';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';

function Configurator() {
  const { formatMessage: msg } = useIntl();

  const { getValue: conf } = useConf(useGetConfigurationQuery);

  const { controller, dispatch } = useMaterialUIController();
  const {
    openConfigurator,
    fixedNavbar,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    primaryColor,
    secondaryColor
  } = controller;
  const [disabled, setDisabled] = useState(false);

  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener('resize', handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleDisabled);
  }, []);

  const customPrimaryColor = primaryColor;
  const customSecondaryColor = secondaryColor;

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
  const handleTransparentSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, true);
  };
  const handleWhiteSidenav = () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
  };
  const handleDarkSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
  };
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);
  const handleDarkMode = () => {
    // If default colors are used, updates them according to their values in conf
    let confPrimaryColor = null;
    if (darkMode && customPrimaryColor === conf(`theme.dark.color.primary`)) {
      confPrimaryColor = conf(`theme.light.color.primary`);
    } else if (!darkMode && customPrimaryColor === conf(`theme.light.color.primary`)) {
      confPrimaryColor = conf(`theme.dark.color.primary`);
    }
    if (confPrimaryColor) {
      setPrimaryColor(dispatch, confPrimaryColor);
    }

    let confSecondaryColor = null;
    if (darkMode && customSecondaryColor === conf(`theme.dark.color.secondary`)) {
      confSecondaryColor = conf(`theme.light.color.secondary`);
    } else if (!darkMode && customSecondaryColor === conf(`theme.light.color.secondary`)) {
      confSecondaryColor = conf(`theme.dark.color.secondary`);
    }
    if (confSecondaryColor) {
      setSecondaryColor(dispatch, confSecondaryColor);
    }

    setDarkMode(dispatch, !darkMode);
  };

  const handlePrimaryPickerColorValueChange = (value) => setPrimaryColor(dispatch, value);
  const handleSecondaryPickerColorValueChange = (value) => setSecondaryColor(dispatch, value);
  const handleResetButtonClick = (value) => resetTheme(dispatch, value);
  // sidenav type buttons styles
  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth }
  }) => ({
    height: pxToRem(39),
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,

    '&:hover, &:focus, &:focus:not(:hover)': {
      background: darkMode ? background.sidenav : white.main,
      color: darkMode ? white.main : dark.main,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`
    }
  });

  // sidenav type active button styles
  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background }
  }) => ({
    height: pxToRem(39),
    background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,

    '&:hover, &:focus, &:focus:not(:hover)': {
      background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
      color: darkMode ? background.sidenav : white.main
    }
  });

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={4}
        pb={0.5}
        px={3}
      >
        <MDBox>
          <MDTypography variant="h5">{msg({ id: 'configurator.title' })}</MDTypography>
        </MDBox>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: 'currentColor',
            strokeWidth: '2px',
            cursor: 'pointer',
            transform: 'translateY(5px)'
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </MDBox>

      <Divider />

      <MDBox pt={0.5} pb={3} px={3}>
        <MDBox>
          <MDTypography variant="h6">{msg({ id: 'configurator.color.main' })}</MDTypography>

          <PopoverPicker
            initialColor={customPrimaryColor}
            onColorChange={handlePrimaryPickerColorValueChange}
          />
        </MDBox>

        <MDBox>
          <MDTypography variant="h6">{msg({ id: 'configurator.color.secondary' })}</MDTypography>

          <PopoverPicker
            initialColor={customSecondaryColor}
            onColorChange={handleSecondaryPickerColorValueChange}
          />
        </MDBox>

        <MDBox mt={3} lineHeight={1}>
          <MDTypography variant="h6">{msg({ id: 'configurator.sidenav.type' })}</MDTypography>
          <MDTypography variant="button" color="text">
            {msg({ id: 'configurator.sidenav.type.desc' })}
          </MDTypography>

          <MDBox
            sx={{
              display: 'flex',
              mt: 2,
              mr: 1
            }}
          >
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleDarkSidenav}
              disabled={disabled}
              fullWidth
              sx={
                !transparentSidenav && !whiteSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              {msg({ id: 'configurator.sidenav.type.dark' })}
            </MDButton>
            <MDBox sx={{ mx: 1, width: '8rem', minWidth: '8rem' }}>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleTransparentSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  transparentSidenav && !whiteSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                {msg({ id: 'configurator.sidenav.type.transparent' })}
              </MDButton>
            </MDBox>
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleWhiteSidenav}
              disabled={disabled}
              fullWidth
              sx={
                whiteSidenav && !transparentSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              {msg({ id: 'configurator.sidenav.type.white' })}
            </MDButton>
          </MDBox>
        </MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          lineHeight={1}
        >
          <MDTypography component="label" htmlFor="fixed-nav-bar-switch" variant="h6">
            {msg({ id: 'configurator.navbar.fixed' })}
          </MDTypography>

          <Switch id="fixed-nav-bar-switch" checked={fixedNavbar} onChange={handleFixedNavbar} />
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography component="label" htmlFor="theme-switch" variant="h6">
            {msg({ id: 'configurator.theme' })}
          </MDTypography>

          <Switch id="theme-switch" checked={darkMode} onChange={handleDarkMode} />
        </MDBox>
        <MDBox display="flex" justifyContent="end" mt={2}>
          <MDButton
            id="resetFormButton"
            type="reset"
            color="light"
            variant="contained"
            onClick={handleResetButtonClick}
          >
            {msg({ id: 'action.reset' })}
          </MDButton>
        </MDBox>
      </MDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
